import { useCallback, useEffect, useRef, useState } from 'react';
import { purchasingAPI, ContractPriceImportRun, ContractPriceImportRunStatus } from '../../lib/api';

/** A run in one of these states never changes again. */
const FINISHED: ContractPriceImportRunStatus[] = ['completed', 'failed'];
const POLL_MS = 2000;
/** How long a run may wait for a worker before the modal says it can be closed. */
const STALL_AFTER_MS = 60000;
/** Failed polls in a row before the modal says it has lost contact. */
const LOST_CONTACT_AFTER = 3;

export interface ContractPriceImportRunTracker {
  /** The latest state of the tracked run, or null when none is tracked. */
  run: ContractPriceImportRun | null;
  /** True while the run is still queued after the stall threshold. */
  stalled: boolean;
  /** True when the run's state cannot be read (repeated poll failures, or a 4xx). */
  lostContact: boolean;
  /** Start following a run (the confirm response); polls until it finishes. */
  track: (run: ContractPriceImportRun) => void;
  /** Stop polling and forget the run. */
  reset: () => void;
}

/**
 * CR-8663: follows a Price Book import run until it is completed or failed.
 *
 * The confirm request only queues the import; a worker writes the rows and
 * recalculates PPV. This polls the run so the modal can show where it is up
 * to. A failed poll is not a failed import: polling carries on through
 * network errors, and `lostContact` tells the user the import may still be
 * running. A 4xx will not cure itself, so it stops polling.
 *
 * @param pollMs - Delay between polls.
 * @param stallAfterMs - How long a queued run waits before `stalled` is set.
 * @returns The tracked run, whether it has stalled or lost contact, and track/reset controls.
 *
 * @example
 * const { run, track, reset } = useContractPriceImportRun();
 * const response = await purchasingAPI.importContractPricesConfirm({ rows });
 * track(response.data);
 */
export function useContractPriceImportRun(
  pollMs: number = POLL_MS,
  stallAfterMs: number = STALL_AFTER_MS,
): ContractPriceImportRunTracker {
  const [run, setRun] = useState<ContractPriceImportRun | null>(null);
  const [stalled, setStalled] = useState(false);
  const [lostContact, setLostContact] = useState(false);
  const failures = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const trackedId = useRef<number | null>(null);
  const trackedSince = useRef(0);

  const stop = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const poll = useCallback(async (runId: number) => {
    try {
      const response = await purchasingAPI.getContractPriceImportRun(runId);
      if (trackedId.current !== runId) return;
      failures.current = 0;
      setLostContact(false);
      setRun(response.data);
      setStalled(
        response.data.run_status === 'queued' && Date.now() - trackedSince.current >= stallAfterMs,
      );
      if (FINISHED.includes(response.data.run_status)) return;
    } catch (err: unknown) {
      console.error('Failed to read the import status:', err);
      if (trackedId.current !== runId) return;
      failures.current += 1;
      const httpStatus = (err as { response?: { status?: number } }).response?.status;
      if (httpStatus !== undefined && httpStatus >= 400 && httpStatus < 500) {
        setLostContact(true);
        return;
      }
      if (failures.current >= LOST_CONTACT_AFTER) setLostContact(true);
    }
    timer.current = setTimeout(() => { void poll(runId); }, pollMs);
  }, [pollMs, stallAfterMs]);

  const track = useCallback((initial: ContractPriceImportRun) => {
    stop();
    trackedId.current = initial.run_id;
    trackedSince.current = Date.now();
    failures.current = 0;
    setRun(initial);
    setStalled(false);
    setLostContact(false);
    if (!FINISHED.includes(initial.run_status)) {
      timer.current = setTimeout(() => { void poll(initial.run_id); }, pollMs);
    }
  }, [poll, pollMs, stop]);

  const reset = useCallback(() => {
    stop();
    trackedId.current = null;
    setRun(null);
    setStalled(false);
    setLostContact(false);
  }, [stop]);

  // Unmounting also forgets the run, so a poll already in flight does not
  // schedule another one when it lands.
  useEffect(() => () => {
    stop();
    trackedId.current = null;
  }, [stop]);

  return { run, stalled, lostContact, track, reset };
}
